import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import { Clock, Filter, RefreshCw, TrendingUp, Heart, MessageCircle, Search, ChevronDown, ExternalLink, Share2, Music, Play, Pause, Eye } from 'lucide-react';
import AccessLevelBadge from '../components/AccessLevelBadge';
import { getAccessLabel, isGated } from '../utils/accessLabels';
import { profileThumb, profileThumbFallback, optimizedCover } from '../utils/imageHelpers';
import { useAuth } from '../context/AuthContext';
import PageHeader from '../components/PageHeader';
import SignupInviteModal from '../components/SignupInviteModal';
import ShareModal from '../components/ShareModal';
import AudioPlayer from '../components/AudioPlayer';
import AccessRequiredModal from '../components/AccessRequiredModal';
import api from '../services/api';
import trackingService from '../services/tracking';

const Feed = () => {
    const navigate = useNavigate();
    const { filter: urlFilter } = useParams();
    const location = useLocation();
    const { user, isAuthenticated, hasActiveSubscription } = useAuth();
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filter, setFilter] = useState(() => {
        // Initialize filter from URL parameter
        const urlToFilter = {
            'posts': 'post',
            'audio': 'audio',
            'videos': 'video',
            'images': 'image',
            'albums': 'album'
        };
        return urlToFilter[urlFilter] || 'all';
    });
    const [postComments, setPostComments] = useState({});
    const [signupInvite, setSignupInvite] = useState({ isOpen: false, contentType: 'content' });
    const [shareModal, setShareModal] = useState({ isOpen: false, post: null });
    const [accessModal, setAccessModal] = useState({ isOpen: false, item: null });
    const [users, setUsers] = useState([]);
    const [userSearchTerm, setUserSearchTerm] = useState('');
    const [showUserDropdown, setShowUserDropdown] = useState(false);
    const [expandedItems, setExpandedItems] = useState(new Set());
    const [showFilterDropdown, setShowFilterDropdown] = useState(false);
    const [playingVideoId, setPlayingVideoId] = useState(null);
    const videoRefs = useRef({});
    // Sort controls — clicking the active sort option again reverses direction
    // (e.g. Date starts "most recent first", click it again for "least recent first").
    const [sortBy, setSortBy] = useState('date'); // 'date' | 'views' | 'likes'
    const [sortDirection, setSortDirection] = useState('desc'); // 'desc' | 'asc'
    const [showSortOptions, setShowSortOptions] = useState(false);

    const handleSortClick = (criterion) => {
        if (sortBy === criterion) {
            setSortDirection(prev => (prev === 'desc' ? 'asc' : 'desc'));
        } else {
            setSortBy(criterion);
            setSortDirection('desc');
        }
    };

    const sortedPosts = React.useMemo(() => {
        const getValue = (post) => {
            switch (sortBy) {
                case 'views': return post.play_count || 0;
                case 'likes': return post.like_count || 0;
                case 'date':
                default: return new Date(post.created_at).getTime();
            }
        };
        const sorted = [...posts].sort((a, b) => getValue(b) - getValue(a)); // desc baseline
        return sortDirection === 'asc' ? sorted.reverse() : sorted;
    }, [posts, sortBy, sortDirection]);

    const toggleExpanded = (key, e) => {
        e.stopPropagation();
        setExpandedItems(prev => {
            const next = new Set(prev);
            next.has(key) ? next.delete(key) : next.add(key);
            return next;
        });
    };

    const needsExpansion = (text) =>
        text && (text.split('\n').length > 8 || text.length > 500);

    useEffect(() => {
        fetchAllMediaPosts();
    }, [filter]);

    useEffect(() => {
        fetchUsers();
    }, []);

    // Handle URL parameter changes (back/forward navigation)
    useEffect(() => {
        const urlToFilter = {
            'posts': 'post',
            'audio': 'audio',
            'videos': 'video',
            'images': 'image',
            'albums': 'album'
        };
        const newFilter = urlToFilter[urlFilter] || 'all';
        
        if (newFilter !== filter) {
            setFilter(newFilter);
        }
    }, [urlFilter]);

    // Update page metadata for SEO
    useEffect(() => {
        const updateMetaTags = async () => {
            try {
                // Fetch site settings from database
                const response = await api.get('/site-settings');
                let siteTitle = '';
                let siteBranding = '';
                let siteName = '';
                
                if (response.data.success) {
                    const data = response.data.data;
                    siteTitle = data.site_title || '';
                    siteBranding = data.site_branding || '';
                    siteName = data.site_title || '';
                }
                
                const filterMetadata = {
                    all: {
                        title: 'Feed',
                        description: 'Discover the latest music, videos, and posts from artists in The Deep Wave community.',
                        keywords: 'the deep wave, community feed, music posts, artist content, creative community, videos, artwork, social media',
                        ogDescription: 'Explore the latest content from The Deep Wave creative community'
                    },
                    post: {
                        title: 'Posts Feed',
                        description: 'Latest text posts and discussions from The Deep Wave community. Join conversations, share thoughts, and connect with artists and creators.',
                        keywords: 'the deep wave, posts, discussions, community, text posts, social media, conversations',
                        ogDescription: 'Join the conversation with posts from The Deep Wave community'
                    },
                    audio: {
                        title: 'Music Feed',
                        description: 'Latest music and audio content from The Deep Wave artists. Discover new tracks, demos, and musical creations from our talented community.',
                        keywords: 'the deep wave, music, audio, tracks, songs, musicians, artists, music community, streaming',
                        ogDescription: 'Discover the latest music from The Deep Wave artists'
                    },
                    video: {
                        title: 'Videos Feed',
                        description: 'Latest video content from The Deep Wave community. Watch music videos, behind-the-scenes content, tutorials, and creative videos from artists.',
                        keywords: 'the deep wave, videos, music videos, content creators, visual art, entertainment, streaming',
                        ogDescription: 'Watch the latest videos from The Deep Wave creative community'
                    },
                    image: {
                        title: 'Artwork Feed',
                        description: 'Latest artwork and visual creations from The Deep Wave artists. Explore digital art, photography, album covers, and creative visual content.',
                        keywords: 'the deep wave, artwork, visual art, digital art, photography, album covers, creative images, art community',
                        ogDescription: 'Explore stunning artwork from The Deep Wave visual artists'
                    },
                    album: {
                        title: 'Albums Feed',
                        description: 'Latest albums from The Deep Wave artists. Explore curated collections of music, videos, and artwork from our creative community.',
                        keywords: 'the deep wave, albums, collections, music albums, artist albums, creative community',
                        ogDescription: 'Discover the latest albums from The Deep Wave community'
                    }
                };
                
                const metadata = filterMetadata[filter] || filterMetadata.all;
                
                // Construct dynamic title from database values
                const pageTitle = siteTitle && siteBranding 
                    ? `${metadata.title} - 🌊 ${siteTitle} - ${siteBranding}`
                    : `${metadata.title}`;
                
                // Update page title
                document.title = pageTitle;
                
                // Update meta description
                const metaDescription = document.querySelector('meta[name="description"]');
                if (metaDescription) {
                    metaDescription.setAttribute('content', metadata.description);
                }

                // Update or create keywords meta tag
                updateMetaTag('keywords', metadata.keywords, 'name');

                // Update Open Graph tags
                const ogTitle = siteTitle ? `${metadata.title} | ${siteTitle}` : metadata.title;
                updateMetaTag('og:title', ogTitle);
                updateMetaTag('og:description', metadata.ogDescription);
                updateMetaTag('og:url', window.location.href);
                updateMetaTag('og:type', 'website');
                updateMetaTag('og:site_name', siteName);
                updateMetaTag('og:image', 'https://thedeepwave.com/og-image.jpg');
                updateMetaTag('og:image:alt', 'The Deep Wave - Creative Community Platform');

                // Twitter Card tags
                updateMetaTag('twitter:card', 'summary_large_image', 'name');
                updateMetaTag('twitter:title', ogTitle, 'name');
                updateMetaTag('twitter:description', metadata.ogDescription, 'name');
                updateMetaTag('twitter:image', 'https://thedeepwave.com/og-image.jpg', 'name');

                // Additional SEO tags
                updateMetaTag('robots', 'index, follow', 'name');
                updateMetaTag('author', siteName, 'name');
                updateMetaTag('viewport', 'width=device-width, initial-scale=1.0', 'name');
            } catch (error) {
                console.error('Failed to fetch site settings for meta tags:', error);
                // Fallback to basic title if API fails
                document.title = `${filter === 'all' ? 'Feed' : `${filter} Feed`}`;
            }
        };
        
        updateMetaTags();
    }, [filter]);

    const updateMetaTag = (property, content, type = 'property') => {
        let selector = type === 'name' ? `meta[name="${property}"]` : `meta[property="${property}"]`;
        let tag = document.querySelector(selector);
        
        if (!tag) {
            tag = document.createElement('meta');
            if (type === 'name') {
                tag.name = property;
            } else {
                tag.property = property;
            }
            document.head.appendChild(tag);
        }
        tag.content = content;
    };

    const fetchAllMediaPosts = async () => {
        try {
            setLoading(true);
            setError(null);

            // Fetch all content types from their respective endpoints using the API service
            let songsRes, videosRes, imagesRes, postsRes, albumsRes;

            try {
                [songsRes, videosRes, imagesRes, postsRes, albumsRes] = await Promise.all([
                    api.get('/upload/public/audio?limit=50'),
                    api.get('/upload/public/video?limit=50'),
                    api.get('/upload/public/image?limit=50'),
                    api.get('/posts/public?limit=50'),
                    api.get('/albums/public?limit=50')
                ]);
            } catch (fetchError) {
                console.error('Error in Promise.all:', fetchError);
                // Try fetching individually if Promise.all fails
                songsRes = await api.get('/upload/public/audio?limit=50').catch(e => ({ data: { success: false } }));
                videosRes = await api.get('/upload/public/video?limit=50').catch(e => ({ data: { success: false } }));
                imagesRes = await api.get('/upload/public/image?limit=50').catch(e => ({ data: { success: false } }));
                postsRes = await api.get('/posts/public?limit=50').catch(e => {
                    console.error('Posts API error:', e);
                    return { data: { success: false } };
                });
                albumsRes = await api.get('/albums/public?limit=50').catch(e => ({ data: { success: false } }));
            }

            // Combine all posts with type indicators
            const allPosts = [];

            console.log('API Responses:', { 
                songs: songsRes.data, 
                videos: videosRes.data, 
                images: imagesRes.data,
                posts: postsRes.data
            });

            // Log sample data to understand profile image fields
            if (songsRes.data.success && songsRes.data.data && songsRes.data.data.length > 0) {
                console.log('Sample song data structure:', songsRes.data.data[0]);
            }
            if (postsRes.data.success && postsRes.data.data && postsRes.data.data.length > 0) {
                console.log('Sample post data structure:', postsRes.data.data[0]);
            }

            console.log('Posts response details:', postsRes.data);

            // First, collect all attachment file URLs from posts to avoid duplicates
            const postAttachmentUrls = new Set();
            if (postsRes.data.success && postsRes.data.data) {
                postsRes.data.data.forEach(post => {
                    if (post.attachments && post.attachments.length > 0) {
                        post.attachments.forEach(attachment => {
                            postAttachmentUrls.add(attachment.file_url);
                        });
                    }
                });
            }

            console.log('Post attachment URLs to exclude:', postAttachmentUrls);

            // Add media with comments
            if (songsRes.data.success && songsRes.data.data) {
                console.log(`Adding ${songsRes.data.data.length} songs`);
                const songsWithComments = await Promise.all(
                    songsRes.data.data.map(async (song) => {
                        // Only add if it's not a post attachment
                        if (postAttachmentUrls.has(song.file_url)) return null;
                        
                        let comments = [];
                        try {
                            const commentsRes = await api.get(`/upload/${song.id}/comments?limit=3`);
                            if (commentsRes.data.success) {
                                comments = commentsRes.data.data;
                            }
                        } catch (err) {
                            console.error(`Error fetching comments for song ${song.id}:`, err);
                        }
                        return { ...song, mediaType: 'audio', previewComments: comments };
                    })
                );
                songsWithComments.filter(Boolean).forEach(song => {
                    allPosts.push(song);
                });
            }

            if (videosRes.data.success && videosRes.data.data) {
                console.log(`Adding ${videosRes.data.data.length} videos`);
                const videosWithComments = await Promise.all(
                    videosRes.data.data.map(async (video) => {
                        // Only add if it's not a post attachment
                        if (postAttachmentUrls.has(video.file_url)) return null;
                        
                        let comments = [];
                        try {
                            const commentsRes = await api.get(`/upload/${video.id}/comments?limit=3`);
                            if (commentsRes.data.success) {
                                comments = commentsRes.data.data;
                            }
                        } catch (err) {
                            console.error(`Error fetching comments for video ${video.id}:`, err);
                        }
                        return { ...video, mediaType: 'video', previewComments: comments };
                    })
                );
                videosWithComments.filter(Boolean).forEach(video => {
                    allPosts.push(video);
                });
            }

            if (imagesRes.data.success && imagesRes.data.data) {
                console.log(`Adding ${imagesRes.data.data.length} images`);
                const imagesWithComments = await Promise.all(
                    imagesRes.data.data.map(async (image) => {
                        // Only add if it's not a post attachment
                        if (postAttachmentUrls.has(image.file_url)) return null;
                        
                        let comments = [];
                        try {
                            const commentsRes = await api.get(`/upload/${image.id}/comments?limit=3`);
                            if (commentsRes.data.success) {
                                comments = commentsRes.data.data;
                            }
                        } catch (err) {
                            console.error(`Error fetching comments for image ${image.id}:`, err);
                        }
                        return { ...image, mediaType: 'image', previewComments: comments };
                    })
                );
                imagesWithComments.filter(Boolean).forEach(image => {
                    allPosts.push(image);
                });
            }

            // Add albums — no comment thread of their own, just likes/views
            if (albumsRes.data.success && albumsRes.data.data) {
                console.log(`Adding ${albumsRes.data.data.length} albums`);
                albumsRes.data.data.forEach(album => {
                    allPosts.push({ ...album, mediaType: 'album', previewComments: [] });
                });
            }

            // Add text/image posts from posts system
            if (postsRes.data.success && postsRes.data.data) {
                console.log(`Adding ${postsRes.data.data.length} posts`);
                // Fetch comments for each post
                const postsWithComments = await Promise.all(
                    postsRes.data.data.map(async (post) => {
                        let comments = [];
                        try {
                            const commentsRes = await api.get(`/posts/${post.id}/comments?limit=3`);
                            if (commentsRes.data.success) {
                                comments = commentsRes.data.data;
                            }
                        } catch (err) {
                            console.error(`Error fetching comments for post ${post.id}:`, err);
                        }
                        return { ...post, mediaType: 'post', previewComments: comments };
                    })
                );
                postsWithComments.forEach(post => {
                    allPosts.push(post);
                });
            }

            console.log(`Total posts combined: ${allPosts.length}`);

            // Fetch user profile data for all unique users in the posts
            const userProfilesMap = new Map();
            const uniqueUsernames = new Set();

            // Collect all unique usernames
            allPosts.forEach(post => {
                if (post.mediaType === 'post') {
                    uniqueUsernames.add(post.author_username);
                } else {
                    uniqueUsernames.add(post.username);
                }
            });

            console.log('Unique usernames for profile fetch:', Array.from(uniqueUsernames));

            // Fetch profile data for each unique user
            try {
                const profilePromises = Array.from(uniqueUsernames).map(async (username) => {
                    try {
                        const profileRes = await api.get(`/profile/${username}`);
                        if (profileRes.data.success) {
                            return { username, profile: profileRes.data.profile };
                        }
                    } catch (err) {
                        console.error(`Error fetching profile for ${username}:`, err);
                    }
                    return null;
                });

                const profileResults = await Promise.all(profilePromises);
                profileResults.forEach(result => {
                    if (result) {
                        userProfilesMap.set(result.username, result.profile);
                    }
                });

                console.log('Fetched user profiles:', userProfilesMap);
            } catch (err) {
                console.error('Error fetching user profiles:', err);
            }

            // Merge profile data into posts
            const postsWithProfiles = allPosts.map(post => {
                const username = post.mediaType === 'post' ? post.author_username : post.username;
                const userProfile = userProfilesMap.get(username);
                
                if (userProfile && userProfile.profilePictureUrl) {
                    if (post.mediaType === 'post') {
                        return {
                            ...post,
                            author_avatar: userProfile.profilePictureUrl
                        };
                    } else {
                        return {
                            ...post,
                            profilePictureUrl: userProfile.profilePictureUrl,
                            avatar: userProfile.profilePictureUrl
                        };
                    }
                }
                return post;
            });

            // Sort by creation date (most recent first)
            const sortedPosts = postsWithProfiles.sort((a, b) => 
                new Date(b.created_at) - new Date(a.created_at)
            );

            // Apply filter if not 'all'
            const filteredPosts = filter === 'all' 
                ? sortedPosts 
                : sortedPosts.filter(post => post.mediaType === filter);

            setPosts(filteredPosts);
        } catch (err) {
            console.error('Error fetching media posts:', err);
            setError(`Failed to load posts: ${err.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await api.get('/users/public');
            if (response.data.success) {
                setUsers(response.data.data);
            }
        } catch (err) {
            console.error('Error fetching users:', err);
        }
    };

    const handleFilterChange = (newFilter) => {
        setFilter(newFilter);
        // Update URL with user-friendly names
        const filterToUrl = {
            'post': 'posts',
            'audio': 'audio',
            'video': 'videos',
            'image': 'images',
            'album': 'albums'
        };
        
        const urlFilter = filterToUrl[newFilter];
        const newPath = newFilter === 'all' ? '/feed' : `/feed/${urlFilter}`;
        navigate(newPath, { replace: true });
    };

    const formatTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInSeconds = Math.floor((now - date) / 1000);

        if (diffInSeconds < 60) return 'Just now';
        if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
        if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
        if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
        if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 604800)}w ago`;
        return date.toLocaleDateString();
    };

    const getMediaIcon = (mediaType) => {
        switch (mediaType) {
            case 'audio': return '🎵';
            case 'video': return '🎥';
            case 'image': return '🖼️';
            case 'post': return '💬';
            case 'album': return '💿';
            default: return '📄';
        }
    };

    const getMediaTypeColor = (mediaType) => {
        switch (mediaType) {
            case 'audio': return 'border-blue-500/30 bg-blue-500/5';
            case 'video': return 'border-green-500/30 bg-green-500/5';
            case 'image': return 'border-cyan-500/30 bg-cyan-500/5';
            case 'post': return 'border-purple-500/30 bg-purple-500/5';
            case 'album': return 'border-indigo-500/30 bg-indigo-500/5';
            default: return 'border-gray-500/30 bg-gray-500/5';
        }
    };

    const handlePostClick = (post) => {
        if (post.mediaType === 'image') {
            // Use stored_filename, original_filename, or id as fallback
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/image/${post.username}/${filename}`);
        } else if (post.mediaType === 'video') {
            // Use stored_filename, original_filename, or id as fallback
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/video/${post.username}/${filename}`);
        } else if (post.mediaType === 'audio') {
            // Use stored_filename, original_filename, or id as fallback
            const filename = post.stored_filename || post.original_filename || post.id;
            navigate(`/view/audio/${post.username}/${filename}`);
        } else if (post.mediaType === 'post') {
            // Navigate to the individual post page
            navigate(`/view/post/${post.author_username}/${post.id}`);
        } else if (post.mediaType === 'album') {
            navigate(`/album/${post.username}/${post.slug || post.id}`);
        }
    };

    const handleLike = async (post) => {
        console.log('🚀 FEED LIKE BUTTON CLICKED!');
        console.log('🚀 Post data before like:', {
            id: post.id,
            mediaType: post.mediaType,
            like_count: post.like_count,
            user_has_liked: post.user_has_liked,
            username: post.username || post.author_username
        });
        
        if (!isAuthenticated) {
            console.log('🚀 User not authenticated in feed');
            setSignupInvite({ isOpen: true, contentType: post.mediaType || 'content' });
            return;
        }

        try {
            let response;
            let endpoint;
            if (post.mediaType === 'post') {
                endpoint = `/posts/${post.id}/like`;
                response = await api.post(endpoint);
            } else if (post.mediaType === 'album') {
                endpoint = `/albums/${post.id}/like`;
                response = await api.post(endpoint);
            } else {
                // For audio, video, images
                endpoint = `/upload/${post.id}/like`;
                response = await api.post(endpoint);
            }
            
            console.log('🚀 Feed like request to:', endpoint);
            console.log('🚀 Feed like response:', response.data);
            
            if (response.data.success) {
                const newLikeData = {
                    like_count: response.data.data.likeCount,
                    user_has_liked: response.data.data.liked
                };
                console.log('🚀 Updating feed post with like data:', newLikeData);
                
                // Update the post in the posts array
                setPosts(prev => prev.map(p => 
                    p.id === post.id && p.mediaType === post.mediaType
                        ? { 
                            ...p, 
                            ...newLikeData
                        }
                        : p
                ));
                
                console.log('🚀 Feed post like update completed');
            } else {
                console.error('🚀 Feed like API returned success=false:', response.data);
            }
        } catch (err) {
            console.error('🚀 Feed like error:', err);
        }
    };

    const handleMediaPlay = (mediaElement, uploadId) => {
        console.log('🎵 Media play started for upload:', uploadId);
        trackingService.startPlayTracking(uploadId, mediaElement);
    };

    const handleVideoPlayPause = (e, postId) => {
        e.stopPropagation();
        const videoEl = videoRefs.current[postId];
        if (!videoEl) return;
        if (playingVideoId === postId) {
            videoEl.pause();
            trackingService.stopPlayTracking(postId);
            setPlayingVideoId(null);
        } else {
            if (playingVideoId && videoRefs.current[playingVideoId]) {
                videoRefs.current[playingVideoId].pause();
                trackingService.stopPlayTracking(playingVideoId);
            }
            setPlayingVideoId(postId);
            videoEl.play();
            handleMediaPlay(videoEl, postId);
        }
    };

    const handleCommentLike = async (comment, post) => {
        if (!isAuthenticated) {
            setSignupInvite({ isOpen: true, contentType: post.mediaType || 'content' });
            return;
        }

        try {
            let response;
            if (post.mediaType === 'post') {
                response = await api.post(`/posts/comments/${comment.id}/like`);
            } else {
                // For audio, video, images comments
                response = await api.post(`/upload/comments/${comment.id}/like`);
            }
            
            if (response.data.success) {
                // Update the comment in the post's comments array
                setPosts(prev => prev.map(p => 
                    p.id === post.id && p.mediaType === post.mediaType
                        ? {
                            ...p,
                            previewComments: p.previewComments.map(c => 
                                c.id === comment.id 
                                    ? {
                                        ...c,
                                        like_count: response.data.data.likeCount,
                                        user_has_liked: response.data.data.liked
                                    }
                                    : c
                            )
                        }
                        : p
                ));
            }
        } catch (err) {
            console.error('Error toggling comment like:', err);
        }
    };

    const handleShare = (post) => {
        setShareModal({ isOpen: true, post });
    };

    return (
        <div className="min-h-screen bg-gray-900">
            <PageHeader 
                title={filter === 'all' ? 'Feed' : `${filter === 'post' ? 'Posts' : filter === 'audio' ? 'Music' : filter === 'video' ? 'Videos' : filter === 'album' ? 'Albums' : 'Artwork'} Feed`}
                subtitle={`Latest ${filter === 'all' ? 'posts and media' : filter === 'post' ? 'posts' : filter === 'audio' ? 'music' : filter === 'video' ? 'videos' : filter === 'album' ? 'albums' : 'artwork'} from The Deep Wave community • ${posts.length} items`}
                icon={<Clock className="w-8 h-8" />}
            />

            <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
                {/* Filter Controls */}
                <div className="mb-8 space-y-4 lg:space-y-0">
                    {/* Header Row */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Filter className="w-5 h-5 text-gray-400" />
                            <span className="text-gray-400 font-medium hidden sm:inline">Filter:</span>
                        </div>
                        <button
                            onClick={fetchAllMediaPosts}
                            disabled={loading}
                            className="flex items-center gap-2 px-3 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors disabled:opacity-50 text-sm"
                            title="Refresh Feed"
                        >
                            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                            <span className="hidden sm:inline">Refresh</span>
                        </button>
                    </div>
                    
                    {/* Filter Buttons */}
                    {(() => {
                        const filterOptions = [
                            { id: 'all', label: 'All Posts', icon: '📂' },
                            { id: 'post', label: 'Posts', icon: '💬' },
                            { id: 'audio', label: 'Music', icon: '🎵' },
                            { id: 'video', label: 'Videos', icon: '🎥' },
                            { id: 'image', label: 'Artwork', icon: '🖼️' },
                            { id: 'album', label: 'Albums', icon: '💿' }
                        ];
                        const activeOption = filterOptions.find(o => o.id === filter) || filterOptions[0];
                        return (
                            <div className="relative">
                                {/* Mobile: dropdown */}
                                <div className="sm:hidden">
                                    <button
                                        onClick={() => setShowFilterDropdown(v => !v)}
                                        className="flex items-center justify-between w-full gap-2 px-4 py-2.5 bg-gradient-to-r from-blue-600 to-blue-800 text-white rounded-lg text-sm font-medium"
                                    >
                                        <span className="flex items-center gap-2">
                                            <span>{activeOption.icon}</span>
                                            <span>{activeOption.label}</span>
                                        </span>
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${showFilterDropdown ? 'rotate-180' : ''}`} />
                                    </button>
                                    {showFilterDropdown && (
                                        <div className="absolute top-full left-0 right-0 mt-1 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden z-20 shadow-xl">
                                            {filterOptions.map((filterOption) => (
                                                <button
                                                    key={filterOption.id}
                                                    onClick={() => { handleFilterChange(filterOption.id); setShowFilterDropdown(false); }}
                                                    className={`flex items-center gap-3 w-full px-4 py-3 text-sm transition-colors ${
                                                        filter === filterOption.id
                                                            ? 'bg-blue-600/20 text-blue-400'
                                                            : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                                                    }`}
                                                >
                                                    <span>{filterOption.icon}</span>
                                                    <span>{filterOption.label}</span>
                                                    {filter === filterOption.id && <span className="ml-auto text-blue-400 text-xs">✓</span>}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                                {/* Desktop: horizontal scroll */}
                                <div className="hidden sm:block sm:overflow-x-auto">
                                    <div className="hidden sm:flex gap-2 min-w-max pb-2">
                                        {filterOptions.map((filterOption) => (
                                            <button
                                                key={filterOption.id}
                                                onClick={() => handleFilterChange(filterOption.id)}
                                                className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 whitespace-nowrap ${
                                                    filter === filterOption.id
                                                        ? 'bg-gradient-to-r from-blue-600 to-blue-800 text-white shadow-lg'
                                                        : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50'
                                                }`}
                                            >
                                                <span>{filterOption.icon}</span>
                                                <span>{filterOption.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Sort Controls */}
                    {(() => {
                        const sortOptions = [
                            { id: 'date', label: 'Date', icon: Clock },
                            { id: 'views', label: 'Views', icon: Eye },
                            { id: 'likes', label: 'Likes', icon: Heart },
                        ];
                        const activeSort = sortOptions.find(o => o.id === sortBy) || sortOptions[0];
                        return (
                            <div className="flex items-center gap-2 pt-4 border-t border-gray-800/50">
                                <button
                                    onClick={() => setShowSortOptions(v => !v)}
                                    className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                        showSortOptions
                                            ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                                            : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent'
                                    }`}
                                >
                                    <activeSort.icon className="w-3.5 h-3.5" />
                                    <span>Sort by: {activeSort.label}</span>
                                    <span className="text-xs">{sortDirection === 'desc' ? '↓' : '↑'}</span>
                                    <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${showSortOptions ? 'rotate-180' : ''}`} />
                                </button>

                                {showSortOptions && sortOptions.map(({ id, label, icon: Icon }) => {
                                    const active = sortBy === id;
                                    return (
                                        <button
                                            key={id}
                                            onClick={() => handleSortClick(id)}
                                            title={active ? `Sorted ${sortDirection === 'desc' ? 'newest/highest first — click to reverse' : 'oldest/lowest first — click to reverse'}` : `Sort by ${label}`}
                                            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm transition-all duration-200 ${
                                                active
                                                    ? 'bg-blue-600/20 text-blue-400 border border-blue-500/30'
                                                    : 'bg-gray-800/50 text-gray-400 hover:text-white hover:bg-gray-700/50 border border-transparent'
                                            }`}
                                        >
                                            <Icon className="w-3.5 h-3.5" />
                                            <span>{label}</span>
                                            {active && (
                                                <span className="text-xs">{sortDirection === 'desc' ? '↓' : '↑'}</span>
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        );
                    })()}

                    {/* User Search */}
                    <div className="flex items-center gap-4 pt-4 border-t border-gray-800/50">
                        <div className="flex items-center gap-2 text-gray-400">
                            <span>🌊</span>
                            <span className="text-sm font-medium hidden sm:inline">Browse Artist:</span>
                        </div>
                        
                        <div className="relative flex-1 max-w-sm">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search artists..."
                                    value={userSearchTerm}
                                    onChange={(e) => {
                                        setUserSearchTerm(e.target.value);
                                        setShowUserDropdown(true);
                                    }}
                                    onFocus={() => setShowUserDropdown(true)}
                                    onBlur={() => setTimeout(() => setShowUserDropdown(false), 200)}
                                    className="w-full px-4 py-2 pl-10 pr-10 bg-gray-800/50 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 text-sm"
                                />
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                                <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                            </div>
                            
                            {showUserDropdown && users.length > 0 && (
                                <div className="absolute top-full left-0 right-0 mt-1 bg-gray-800 border border-gray-700 rounded-lg shadow-xl z-50 max-h-64 overflow-y-auto">
                                    <div className="py-2">
                                        {users
                                            .filter(user => 
                                                !userSearchTerm || 
                                                (user.username && user.username.toLowerCase().includes(userSearchTerm.toLowerCase())) ||
                                                (user.display_name && user.display_name.toLowerCase().includes(userSearchTerm.toLowerCase()))
                                            )
                                            .slice(0, 10)
                                            .map(user => (
                                                <button
                                                    key={user.id}
                                                    onClick={() => {
                                                        navigate(`/feed/artist/${user.username}`);
                                                        setUserSearchTerm('');
                                                        setShowUserDropdown(false);
                                                    }}
                                                    className="w-full px-3 py-2 text-left text-sm text-white hover:bg-gray-700 transition-colors flex items-center gap-2"
                                                >
                                                    <div className="w-6 h-6 rounded-full overflow-hidden flex-shrink-0">
                                                        {user.profilePictureUrl ? (
                                                            <img
                                                                src={user.profilePictureUrl}
                                                                alt={user.display_name || user.username}
                                                                width="24"
                                                                height="24"
                                                                className="w-full h-full object-cover"
                                                            />
                                                        ) : (
                                                            <div className="w-full h-full bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                                                                <span className="text-white text-xs">
                                                                    {(user.display_name || user.username)?.charAt(0)}
                                                                </span>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div>
                                                        <div className="font-medium">{user.display_name || user.username}</div>
                                                        {user.display_name && (
                                                            <div className="text-gray-400 text-xs">@{user.username}</div>
                                                        )}
                                                    </div>
                                                </button>
                                            ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Feed Content */}
                {loading && (
                    <div className="space-y-4">
                        {[...Array(6)].map((_, i) => (
                            <div key={i} className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-6 border-y sm:border border-gray-700/50 animate-pulse">
                                <div className="w-full h-64 bg-gray-700 rounded-lg mb-4"></div>
                                <div className="flex items-start gap-4">
                                    <div className="w-12 h-12 bg-gray-700 rounded-full"></div>
                                    <div className="flex-1">
                                        <div className="h-4 bg-gray-700 rounded w-48 mb-2"></div>
                                        <div className="h-3 bg-gray-700 rounded w-32 mb-2"></div>
                                        <div className="h-3 bg-gray-700 rounded w-24"></div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {error && (
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-8 border-y sm:border border-red-500/30 bg-red-500/5 text-center">
                        <div className="text-red-400 text-lg mb-2">⚠️ Error Loading Feed</div>
                        <p className="text-gray-400 mb-4">{error}</p>
                        <button
                            onClick={fetchAllMediaPosts}
                            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                )}

                {!loading && !error && posts.length === 0 && (
                    <div className="glass-card -mx-4 sm:mx-0 rounded-none sm:rounded-2xl p-12 text-center border-y sm:border border-gray-700/50">
                        <div className="text-6xl mb-4">📭</div>
                        <h3 className="text-xl font-bold text-white mb-2">No Posts Found</h3>
                        <p className="text-gray-400">
                            {filter === 'all' 
                                ? "There are no posts in the community feed yet."
                                : `No ${filter} posts found. Try changing the filter.`
                            }
                        </p>
                    </div>
                )}

                {!loading && !error && posts.length > 0 && (
                    <div className="space-y-4 sm:space-y-8">
                        {sortedPosts.map((post, postIndex) => (
                            <div
                                key={`${post.mediaType}-${post.id}`}
                                className={`glass-card p-0 border-y sm:border -mx-4 sm:mx-0 rounded-none sm:rounded-2xl cursor-pointer hover:shadow-2xl transition-all duration-300 sm:hover:scale-[1.02] overflow-hidden ${getMediaTypeColor(post.mediaType)}`}
                                onClick={() => handlePostClick(post)}
                            >
                                {/* Full Content Preview */}
                                <div className="w-full">
                                    {post.mediaType === 'image' ? (
                                        isGated(post.access_level, isAuthenticated, hasActiveSubscription) ? (
                                            <div className="relative overflow-hidden bg-gray-900">
                                                <img
                                                    src={post.optimized_url || post.file_url}
                                                    alt={post.title}
                                                    className="w-full max-h-[600px] object-contain pointer-events-none"
                                                    loading={postIndex === 0 ? 'eager' : 'lazy'}
                                                    fetchPriority={postIndex === 0 ? 'high' : undefined}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'view')}</span>
                                                </div>
                                                <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                            </div>
                                        ) : (
                                            <img
                                                src={post.optimized_url || post.file_url}
                                                alt={post.title}
                                                className="w-full max-h-[600px] object-contain"
                                                loading={postIndex === 0 ? 'eager' : 'lazy'}
                                                fetchPriority={postIndex === 0 ? 'high' : undefined}
                                                onContextMenu={(e) => e.preventDefault()}
                                            />
                                        )
                                    ) : post.mediaType === 'video' ? (
                                        isGated(post.access_level, isAuthenticated, hasActiveSubscription) ? (
                                            <div className="relative overflow-hidden bg-gray-900">
                                                <video
                                                    src={post.file_url}
                                                    poster={optimizedCover(post.cover_image_url)}
                                                    className="w-full max-h-[90vh] object-contain pointer-events-none"
                                                    preload="metadata"
                                                    playsInline
                                                    onContextMenu={(e) => e.preventDefault()}
                                                />
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'view')}</span>
                                                </div>
                                                <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                            </div>
                                        ) : (
                                            <div className="relative bg-gray-900 group" onClick={(e) => e.stopPropagation()}>
                                                <video
                                                    ref={(el) => { videoRefs.current[post.id] = el; }}
                                                    src={post.file_url}
                                                    poster={optimizedCover(post.cover_image_url)}
                                                    className="w-full max-h-[90vh] object-contain"
                                                    preload="metadata"
                                                    playsInline
                                                    onEnded={() => setPlayingVideoId(null)}
                                                    onContextMenu={(e) => e.preventDefault()}
                                                />
                                                <div
                                                    className="absolute inset-0 flex items-center justify-center bg-black/20 opacity-100 group-hover:bg-black/40 transition-colors duration-200 cursor-pointer"
                                                    onClick={(e) => handleVideoPlayPause(e, post.id)}
                                                >
                                                    {playingVideoId === post.id ? (
                                                        <div className="p-4 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                                            <Pause className="w-8 h-8 text-white" />
                                                        </div>
                                                    ) : (
                                                        <div className="p-4 bg-black/50 rounded-full hover:bg-black/70 transition-colors">
                                                            <Play className="w-8 h-8 text-white ml-1" />
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )
                                    ) : post.mediaType === 'audio' ? (() => {
                                        const gated = isGated(post.access_level, isAuthenticated, hasActiveSubscription);
                                        return (
                                        <div className="w-full p-4 bg-gray-900/40">
                                            {post.description && (() => {
                                                const key = `audio-${post.id}`;
                                                const expanded = expandedItems.has(key);
                                                const clamped = needsExpansion(post.description);
                                                return (
                                                    <div className="mb-3">
                                                        <p
                                                            className="text-gray-300 text-sm whitespace-pre-wrap leading-relaxed"
                                                            style={!expanded && clamped ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                                        >
                                                            {post.description}
                                                        </p>
                                                        {clamped && (
                                                            <button
                                                                onClick={(e) => toggleExpanded(key, e)}
                                                                className="mt-2 text-blue-400 hover:text-blue-300 text-xs font-medium transition-colors"
                                                            >
                                                                {expanded ? 'Show less' : 'Read more'}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                            <AudioPlayer
                                                src={post.file_url}
                                                coverImageUrl={post.cover_image_url}
                                                title={post.title}
                                                uploadId={gated ? null : post.id}
                                                locked={gated}
                                                onLockedClick={() => setAccessModal({ isOpen: true, item: post })}
                                            />
                                        </div>
                                        );
                                    })() : post.mediaType === 'album' ? (
                                        isGated(post.access_level, isAuthenticated, hasActiveSubscription) ? (
                                            <div className="relative overflow-hidden bg-gray-900">
                                                {post.cover_image_url ? (
                                                    <img
                                                        src={optimizedCover(post.cover_image_url)}
                                                        alt={post.title}
                                                        className="w-full max-h-[600px] object-contain pointer-events-none"
                                                        loading={postIndex === 0 ? 'eager' : 'lazy'}
                                                        fetchPriority={postIndex === 0 ? 'high' : undefined}
                                                        onContextMenu={(e) => e.preventDefault()}
                                                    />
                                                ) : (
                                                    <div className="w-full h-64 bg-gradient-to-br from-indigo-700 to-indigo-900 flex items-center justify-center pointer-events-none">
                                                        <span className="text-8xl">💿</span>
                                                    </div>
                                                )}
                                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent flex items-end justify-center pb-3">
                                                    <span className="text-white text-xs font-medium bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full">{getAccessLabel(post.access_level, 'view')}</span>
                                                </div>
                                                <AccessLevelBadge accessLevel={post.access_level} isAuthenticated={isAuthenticated} hasActiveSubscription={hasActiveSubscription} />
                                            </div>
                                        ) : (
                                            <div className="relative bg-gray-900">
                                                {post.cover_image_url ? (
                                                    <img
                                                        src={optimizedCover(post.cover_image_url)}
                                                        alt={post.title}
                                                        className="w-full max-h-[600px] object-contain"
                                                        loading={postIndex === 0 ? 'eager' : 'lazy'}
                                                        fetchPriority={postIndex === 0 ? 'high' : undefined}
                                                        onContextMenu={(e) => e.preventDefault()}
                                                    />
                                                ) : (
                                                    <div className="w-full h-64 bg-gradient-to-br from-indigo-700 to-indigo-900 flex items-center justify-center">
                                                        <span className="text-8xl">💿</span>
                                                    </div>
                                                )}
                                                <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm px-3 py-1 rounded-full text-white text-xs font-medium">
                                                    {post.item_count || 0} item{post.item_count === 1 ? '' : 's'}
                                                </div>
                                            </div>
                                        )
                                    ) : post.mediaType === 'post' && post.attachments && post.attachments.length > 0 ? (
                                        <div className="w-full">
                                            <div className={`grid gap-2 ${
                                                post.attachments.slice(0, 2).length === 1 ? 'grid-cols-1' : 'grid-cols-2'
                                            }`}>
                                                {post.attachments.slice(0, 2).map((attachment, index) => (
                                                    <div key={attachment.id} className="relative">
                                                        <img
                                                            src={attachment.optimized_url || attachment.file_url}
                                                            alt={`Post attachment ${index + 1}`}
                                                            className={`w-full ${post.attachments.slice(0, 2).length === 1 ? 'max-h-[600px]' : 'aspect-square'} object-contain`}
                                                            loading={postIndex === 0 ? 'eager' : 'lazy'}
                                                            fetchPriority={postIndex === 0 ? 'high' : undefined}
                                                            onError={(e) => { e.target.onerror = null; e.target.src = attachment.file_url; }}
                                                            onContextMenu={(e) => e.preventDefault()}
                                                        />
                                                        {/* "View More" overlay on 2nd image if there are more images */}
                                                        {index === 1 && post.attachments.length > 2 && (
                                                            <Link
                                                                to={`/view/post/${post.author_username}/${post.id}`}
                                                                className="absolute inset-0 bg-black/60 rounded-lg flex items-center justify-center text-white hover:bg-black/70 transition-colors"
                                                            >
                                                                <div className="text-center">
                                                                    <ExternalLink className="w-6 h-6 mx-auto mb-1" />
                                                                    <div className="text-sm font-medium">
                                                                        +{post.attachments.length - 2} more
                                                                    </div>
                                                                </div>
                                                            </Link>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>

                                            {/* "View Full Post" button if there are more than 2 images */}
                                            {post.attachments.length > 2 && (
                                                <div className="mt-3 text-center">
                                                    <Link
                                                        to={`/view/post/${post.author_username}/${post.id}`}
                                                        className="inline-flex items-center gap-2 px-4 py-2 text-sm text-blue-400 hover:text-blue-300 bg-blue-400/10 hover:bg-blue-400/20 rounded-lg transition-colors"
                                                    >
                                                        <ExternalLink className="w-4 h-4" />
                                                        View all {post.attachments.length} images
                                                    </Link>
                                                </div>
                                            )}

                                            {/* Post text content */}
                                            {post.content && (() => {
                                                const key = `post-${post.id}`;
                                                const expanded = expandedItems.has(key);
                                                const clamped = needsExpansion(post.content);
                                                return (
                                                    <div className="p-6 border-t border-gray-700/50">
                                                        <p
                                                            className="text-white text-base whitespace-pre-wrap leading-relaxed"
                                                            style={!expanded && clamped ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                                        >
                                                            {post.content}
                                                        </p>
                                                        {clamped && (
                                                            <button
                                                                onClick={(e) => toggleExpanded(key, e)}
                                                                className="mt-2 text-gray-400 hover:text-white text-sm font-medium transition-colors"
                                                            >
                                                                {expanded ? 'Show less' : 'Read more'}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : post.mediaType === 'post' ? (
                                        <div className="w-full bg-gradient-to-br from-purple-600 to-purple-800 p-8">
                                            <div className="flex items-start gap-4 mb-4">
                                                <div className="w-16 h-16 bg-purple-500/30 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <span className="text-2xl">💬</span>
                                                </div>
                                                <div className="flex-1">
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            navigate(`/profile/${post.author_username}`);
                                                        }}
                                                        className="text-left"
                                                    >
                                                        <h4 className="font-bold text-white text-lg mb-1 hover:text-purple-200 transition-colors">
                                                            {post.author_display_name || post.author_username}
                                                        </h4>
                                                        <p className="text-purple-200 text-sm mb-4 hover:text-purple-100 transition-colors">
                                                            @{post.author_username}
                                                        </p>
                                                    </button>
                                                </div>
                                            </div>
                                            {(() => {
                                                const key = `post-${post.id}`;
                                                const expanded = expandedItems.has(key);
                                                const clamped = needsExpansion(post.content);
                                                return (
                                                    <div>
                                                        <p
                                                            className="text-white text-lg leading-relaxed whitespace-pre-wrap"
                                                            style={!expanded && clamped ? { display: '-webkit-box', WebkitLineClamp: 8, WebkitBoxOrient: 'vertical', overflow: 'hidden' } : {}}
                                                        >
                                                            {post.content}
                                                        </p>
                                                        {clamped && (
                                                            <button
                                                                onClick={(e) => toggleExpanded(key, e)}
                                                                className="mt-2 text-purple-200 hover:text-white text-sm font-medium transition-colors"
                                                            >
                                                                {expanded ? 'Show less' : 'Read more'}
                                                            </button>
                                                        )}
                                                    </div>
                                                );
                                            })()}
                                        </div>
                                    ) : (
                                        <div className="w-full bg-gradient-to-br from-gray-700 to-gray-800 p-12 flex items-center justify-center">
                                            <div className="text-center">
                                                <span className="text-8xl mb-6 block">{getMediaIcon(post.mediaType)}</span>
                                                <h3 className="text-white font-bold text-2xl mb-2">{post.title}</h3>
                                                <p className="text-gray-300 text-lg">{post.display_name || post.username}</p>
                                                {post.description && (
                                                    <p className="text-gray-400 text-sm mt-4 max-w-lg">{post.description}</p>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Post Metadata Footer */}
                                <div className="p-6">
                                    <div className="flex items-center gap-4">
                                        {/* Creator Avatar with Profile Link */}
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                const username = post.mediaType === 'post' 
                                                    ? post.author_username 
                                                    : post.username;
                                                navigate(`/profile/${username}`);
                                            }}
                                            className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden hover:ring-2 hover:ring-blue-400 transition-all duration-200 hover:scale-105"
                                        >
                                            {post.mediaType === 'post' ? (
                                                post.author_avatar ? (
                                                    <img
                                                        src={profileThumb(post.author_avatar)}
                                                        alt={post.author_display_name || post.author_username}
                                                        width="48"
                                                        height="48"
                                                        className="w-12 h-12 rounded-full object-cover"
                                                        onError={profileThumbFallback(post.author_avatar)}
                                                    />
                                                ) : (
                                                    <span className="text-white font-semibold">
                                                        {(post.author_display_name || post.author_username)?.charAt(0) || 'U'}
                                                    </span>
                                                )
                                            ) : (
                                                (post.avatar || post.profile_image || post.profilePictureUrl || post.profile_picture_url) ? (
                                                    <img
                                                        src={profileThumb(post.avatar || post.profile_image || post.profilePictureUrl || post.profile_picture_url)}
                                                        alt={post.display_name || post.username}
                                                        width="48"
                                                        height="48"
                                                        className="w-12 h-12 rounded-full object-cover"
                                                        onError={profileThumbFallback(post.avatar || post.profile_image || post.profilePictureUrl || post.profile_picture_url)}
                                                    />
                                                ) : (
                                                    <span className="text-white font-semibold">
                                                        {(post.display_name || post.username)?.charAt(0) || 'U'}
                                                    </span>
                                                )
                                            )}
                                        </button>
                                        
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between">
                                                <div>
                                                    {post.mediaType === 'post' ? (
                                                        <>
                                                            <h3 className="text-lg font-bold text-white">
                                                                {post.author_display_name || post.author_username}
                                                            </h3>
                                                            <button
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    navigate(`/profile/${post.author_username}`);
                                                                }}
                                                                className="text-sm text-gray-400 hover:text-blue-300 transition-colors hover:underline"
                                                            >
                                                                @{post.author_username}
                                                            </button>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <h3 className="text-lg font-bold text-white">{post.title}</h3>
                                                            <p className="text-sm text-gray-400">
                                                                by{' '}
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/profile/${post.username}`);
                                                                    }}
                                                                    className="text-blue-400 hover:text-blue-300 transition-colors hover:underline"
                                                                >
                                                                    {post.display_name || post.username}
                                                                </button>
                                                            </p>
                                                        </>
                                                    )}
                                                </div>
                                                <div className="flex items-center gap-4 text-sm text-gray-400">
                                                    <span className="flex items-center gap-2">
                                                        <span>{getMediaIcon(post.mediaType)}</span>
                                                        <span className="capitalize">{post.mediaType}</span>
                                                    </span>
                                                </div>
                                            </div>

                                            {/* Stats and Interaction Row */}
                                            <div className="flex items-center justify-between mt-3">
                                                <div className="flex items-center gap-4">
                                                    {/* Like Button - Always show for all content */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleLike(post);
                                                        }}
                                                        className={`flex items-center gap-1 text-sm transition-colors cursor-pointer ${
                                                            post.user_has_liked 
                                                                ? 'text-red-400 hover:text-red-300' 
                                                                : 'text-gray-400 hover:text-red-400'
                                                        }`}
                                                    >
                                                        <Heart className={`w-4 h-4 ${post.user_has_liked ? 'fill-current' : ''}`} />
                                                        <span>{post.like_count || 0}</span>
                                                    </button>

                                                    {/* Comments - Always show for all content */}
                                                    <div className="flex items-center gap-1 text-sm text-gray-400">
                                                        <MessageCircle className="w-4 h-4" />
                                                        <span>{post.comment_count || 0}</span>
                                                    </div>

                                                    {/* Share Button */}
                                                    <button
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleShare(post);
                                                        }}
                                                        className="flex items-center gap-1 text-sm text-gray-400 hover:text-blue-400 transition-colors cursor-pointer"
                                                    >
                                                        <Share2 className="w-4 h-4" />
                                                        <span>Share</span>
                                                    </button>

                                                    {/* Play/View count for media only */}
                                                    {post.mediaType !== 'post' && Boolean((post.mediaType === 'album' ? post.view_count : post.play_count) > 0) && (
                                                        <span className="flex items-center gap-1 text-sm text-gray-400">
                                                            <span>👁️</span>
                                                            <span>{post.mediaType === 'album' ? post.view_count : post.play_count} {post.mediaType === 'image' || post.mediaType === 'album' ? 'views' : 'plays'}</span>
                                                        </span>
                                                    )}
                                                </div>
                                                
                                                <p className="text-sm text-gray-400">{formatTimeAgo(post.created_at)}</p>
                                            </div>

                                            {/* Additional metadata for images */}
                                            {post.mediaType === 'image' && (post.medium || post.category) && (
                                                <div className="flex gap-2 mt-3">
                                                    {post.category && (
                                                        <span className="px-2 py-1 bg-cyan-500/20 text-cyan-300 text-xs rounded-md">
                                                            {post.category}
                                                        </span>
                                                    )}
                                                    {post.medium && (
                                                        <span className="px-2 py-1 bg-gray-500/20 text-gray-300 text-xs rounded-md">
                                                            {post.medium}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Comments Preview for All Media */}
                                            {post.previewComments && post.previewComments.length > 0 && (
                                                <div className="mt-4 pt-4 border-t border-gray-700/50">
                                                    <h4 className="text-sm font-medium text-gray-300 mb-3">Recent Comments</h4>
                                                    <div className="space-y-3">
                                                        {post.previewComments.map((comment) => (
                                                            <div key={comment.id} className="flex gap-3">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        navigate(`/profile/${comment.username}`);
                                                                    }}
                                                                    className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center flex-shrink-0 hover:ring-2 hover:ring-blue-400 transition-all duration-200 hover:scale-105"
                                                                >
                                                                    {(comment.avatar || comment.profilePictureUrl) ? (
                                                                        <img
                                                                            src={profileThumb(comment.avatar || comment.profilePictureUrl)}
                                                                            alt={comment.display_name || comment.username}
                                                                            width="32"
                                                                            height="32"
                                                                            className="w-8 h-8 rounded-full object-cover"
                                                                            onError={profileThumbFallback(comment.avatar || comment.profilePictureUrl)}
                                                                        />
                                                                    ) : (
                                                                        <span className="text-white font-semibold text-xs">
                                                                            {(comment.display_name || comment.username)?.charAt(0) || 'U'}
                                                                        </span>
                                                                    )}
                                                                </button>
                                                                <div className="flex-1 min-w-0">
                                                                    <div className="flex items-center gap-2 mb-1">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                navigate(`/profile/${comment.username}`);
                                                                            }}
                                                                            className="text-sm font-medium text-blue-400 hover:text-blue-300 transition-colors hover:underline"
                                                                        >
                                                                            {comment.display_name || comment.username}
                                                                        </button>
                                                                        <span className="text-xs text-gray-500">
                                                                            {formatTimeAgo(comment.created_at)}
                                                                        </span>
                                                                    </div>
                                                                    <p className="text-sm text-gray-300 mb-2">
                                                                        {comment.content}
                                                                    </p>
                                                                    <div className="flex items-center gap-3">
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleCommentLike(comment, post);
                                                                            }}
                                                                            disabled={!isAuthenticated}
                                                                            className={`flex items-center gap-1 text-xs transition-colors ${
                                                                                comment.user_has_liked 
                                                                                    ? 'text-red-400 hover:text-red-300' 
                                                                                    : 'text-gray-500 hover:text-red-400'
                                                                            } ${!isAuthenticated ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                                                                        >
                                                                            <Heart className={`w-3 h-3 ${comment.user_has_liked ? 'fill-current' : ''}`} />
                                                                            <span>{comment.like_count || 0}</span>
                                                                        </button>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                    {post.comment_count > 3 && (
                                                        <button
                                                            onClick={() => {
                                                                if (post.mediaType === 'post') {
                                                                    navigate(`/view/post/${post.author_username}/${post.id}`);
                                                                } else {
                                                                    // For media files, navigate to the media viewer
                                                                    const filename = post.stored_filename || post.original_filename || post.id;
                                                                    const username = post.username;
                                                                    navigate(`/view/${post.mediaType}/${username}/${filename}`);
                                                                }
                                                            }}
                                                            className="mt-3 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                                        >
                                                            View all {post.comment_count} comments
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}

                        {/* Load More Message */}
                        {posts.length >= 20 && (
                            <div className="text-center py-8">
                                <p className="text-gray-400 text-sm">
                                    Showing latest {posts.length} posts. More content loading features coming soon!
                                </p>
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Signup Invite Modal */}
            <SignupInviteModal
                isOpen={signupInvite.isOpen}
                onClose={() => setSignupInvite({ isOpen: false, contentType: 'content' })}
                contentType={signupInvite.contentType}
            />

            {/* Share Modal */}
            <ShareModal
                isOpen={shareModal.isOpen}
                onClose={() => setShareModal({ isOpen: false, post: null })}
                mediaTitle={
                    shareModal.post?.mediaType === 'post' 
                        ? (shareModal.post?.content?.substring(0, 50) + '...' || 'Post')
                        : (shareModal.post?.title || 'Media')
                }
                mediaType={shareModal.post?.mediaType || 'content'}
                username={
                    shareModal.post?.mediaType === 'post' 
                        ? shareModal.post?.author_username 
                        : shareModal.post?.username
                }
                mediaId={
                    shareModal.post?.mediaType === 'post'
                        ? shareModal.post?.id
                        : (shareModal.post?.stored_filename || shareModal.post?.original_filename || shareModal.post?.id)
                }
                mediaUrl={
                    shareModal.post?.mediaType === 'album'
                        ? `${window.location.origin}/album/${shareModal.post.username}/${shareModal.post.slug || shareModal.post.id}`
                        : undefined
                }
                imageUrl={shareModal.post?.mediaType === 'album' ? shareModal.post?.cover_image_url : undefined}
                artistName={
                    shareModal.post?.mediaType === 'post'
                        ? (shareModal.post?.author_display_name || shareModal.post?.author_username)
                        : (shareModal.post?.display_name || shareModal.post?.username)
                }
            />

            <AccessRequiredModal
                isOpen={accessModal.isOpen}
                onClose={() => setAccessModal({ isOpen: false, item: null })}
                accessLevel={accessModal.item?.access_level}
                action="listen"
                isAuthenticated={isAuthenticated}
                viewUrl={accessModal.item ? `/view/${accessModal.item.mediaType}/${accessModal.item.username}/${accessModal.item.stored_filename || accessModal.item.original_filename || accessModal.item.id}` : null}
            />
        </div>
    );
};

export default Feed;